import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { StorefrontModules, StorefrontPalette } from '@fetmode/types';
import {
  DEFAULT_STOREFRONT_MODULES,
  DEFAULT_STOREFRONT_PALETTE,
  STOREFRONT_PALETTES,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useStudioModel } from '../studio/index.js';
import './StorefrontAppearanceCard.css';

const PALETTE_LABELS: Record<StorefrontPalette, string> = {
  violet: 'common.studio.settings.paletteViolet',
  pink: 'common.studio.settings.palettePink',
  cyan: 'common.studio.settings.paletteCyan',
  ink: 'common.studio.settings.paletteInk',
  rose: 'common.studio.settings.paletteRose',
};

const MODULE_TOGGLES: ReadonlyArray<{ key: keyof StorefrontModules; label: string }> = [
  { key: 'about', label: 'common.studio.settings.moduleAbout' },
  { key: 'schedule', label: 'common.studio.settings.moduleSchedule' },
  { key: 'clips', label: 'common.studio.settings.moduleClips' },
  { key: 'wishlist', label: 'common.studio.settings.moduleWishlist' },
  { key: 'fanClub', label: 'common.studio.settings.moduleFanClub' },
  { key: 'social', label: 'common.studio.settings.moduleSocial' },
];

type PreviewWidth = 'desktop' | 'phone';

export function StorefrontAppearanceCard(): ReactElement {
  const { api } = useAuth();
  const { profile: studioProfile, setProfile: setStudioProfile } = useStudioModel();
  const [palette, setPalette] = useState<StorefrontPalette>(DEFAULT_STOREFRONT_PALETTE);
  const [modules, setModules] = useState<StorefrontModules>(DEFAULT_STOREFRONT_MODULES);
  const [coverUrl, setCoverUrl] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState<PreviewWidth>('desktop');

  const load = useCallback(async (): Promise<void> => {
    const result = await api.getModelProfile();
    if (!result.profile) {
      return;
    }
    setPalette(result.profile.storefrontPalette);
    setModules(result.profile.storefrontModules);
    setCoverUrl(result.profile.coverUrl ?? '');
    setAvatarUrl(result.profile.avatarUrl ?? '');
    setDisplayName(result.profile.displayName);
    setUsername(result.profile.username);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.profile.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await api.updateModelProfile({
        coverUrl: coverUrl.trim() || null,
        avatarUrl: avatarUrl.trim() || null,
        storefrontPalette: palette,
        storefrontModules: modules,
      });
      setStudioProfile(result.profile);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.profile.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.profile.loading')}</p>;
  }

  const previewName = displayName || studioProfile?.displayName || username;
  const visibleTabs = [
    modules.clips ? t('common.storefront.tabVideos') : null,
    t('common.storefront.tabPosts'),
    modules.clips ? t('common.storefront.tabPics') : null,
    modules.fanClub ? t('common.storefront.tabGoodies') : null,
    modules.social ? t('common.storefront.tabSocials') : null,
    modules.about ? t('common.storefront.about') : null,
    modules.wishlist ? t('common.studio.settings.moduleWishlist') : null,
  ].filter((label): label is string => label !== null);

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1.125rem' }}>{t('common.studio.settings.appearanceTitle')}</h2>
      <p style={{ margin: '0 0 0.5rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.settings.appearanceHint')}
      </p>
      <p style={{ margin: '0 0 0.35rem', fontSize: '0.875rem' }}>
        {t('common.studio.settings.appearanceYouControl')}
      </p>
      <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>
        {t('common.studio.settings.appearancePlatformKeeps')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}
      {saved ? (
        <p style={{ margin: '0 0 0.75rem', color: 'var(--fm-color-secondary)', fontSize: '0.875rem' }}>
          {t('common.studio.settings.appearanceSaved')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '1rem' }}>
        <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
          <legend style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.45rem' }}>
            {t('common.studio.settings.paletteLabel')}
          </legend>
          <div className="storefront-look__palettes">
            {STOREFRONT_PALETTES.map((id) => (
              <button
                key={id}
                type="button"
                className={
                  palette === id
                    ? 'storefront-look__palette storefront-look__palette--on'
                    : 'storefront-look__palette'
                }
                disabled={isSaving}
                onClick={() => {
                  setPalette(id);
                }}
              >
                <span className={`storefront-look__swatch storefront-look__swatch--${id}`} />
                {t(PALETTE_LABELS[id])}
              </button>
            ))}
          </div>
        </fieldset>

        <label>
          {t('common.studio.settings.coverUrl')}
          <input
            className="fm-input"
            value={coverUrl}
            disabled={isSaving}
            onChange={(event) => {
              setCoverUrl(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.settings.avatarUrl')}
          <input
            className="fm-input"
            value={avatarUrl}
            disabled={isSaving}
            onChange={(event) => {
              setAvatarUrl(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
          <span style={{ display: 'block', marginTop: '0.25rem', color: 'var(--fm-color-text-muted)', fontSize: '0.8125rem' }}>
            {t('common.studio.settings.imageUrlHint')}
          </span>
        </label>

        <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
          <legend style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.45rem' }}>
            {t('common.studio.settings.modulesTitle')}
          </legend>
          <div className="storefront-look__modules">
            <p className="storefront-look__locked">{t('common.studio.settings.moduleHeroLocked')}</p>
            <p className="storefront-look__locked">{t('common.studio.settings.moduleWatchLocked')}</p>
            {MODULE_TOGGLES.map((item) => (
              <label key={item.key} className="storefront-look__toggle">
                <input
                  type="checkbox"
                  checked={modules[item.key]}
                  disabled={isSaving}
                  onChange={() => {
                    setModules((current) => ({ ...current, [item.key]: !current[item.key] }));
                  }}
                />
                {t(item.label)}
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <p style={{ margin: '0 0 0.45rem', fontSize: '0.875rem', fontWeight: 600 }}>
            {t('common.studio.settings.previewLabel')}
          </p>
          <div className="storefront-look__preview-bar">
            <Button
              label={t('common.studio.settings.previewDesktop')}
              variant={preview === 'desktop' ? 'secondary' : 'ghost'}
              onClick={() => {
                setPreview('desktop');
              }}
            />
            <Button
              label={t('common.studio.settings.previewMobile')}
              variant={preview === 'phone' ? 'secondary' : 'ghost'}
              onClick={() => {
                setPreview('phone');
              }}
            />
          </div>
          <div
            className={
              preview === 'phone'
                ? 'storefront-look__frame storefront-look__frame--phone'
                : 'storefront-look__frame'
            }
            data-storefront-palette={palette}
          >
            <div className="storefront-look__mini">
              <div
                className="storefront-look__cover"
                style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : undefined}
              />
              <div className="storefront-look__mini-id">
                <div
                  className="storefront-look__avatar"
                  style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined}
                />
                <div>
                  <p className="storefront-look__mini-name">{previewName}</p>
                  <p className="storefront-look__mini-meta">
                    @{username || 'you'} · {t('common.studio.settings.previewLivePill')}
                  </p>
                </div>
              </div>
              <ul className="storefront-look__tabs">
                {visibleTabs.map((label) => (
                  <li key={label} className="storefront-look__tab">
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <Button
          label={
            isSaving
              ? t('common.studio.settings.appearanceSaving')
              : t('common.studio.settings.appearanceSave')
          }
          disabled={isSaving}
          onClick={() => {
            void handleSave();
          }}
        />
        <p style={{ margin: 0, color: 'var(--fm-color-text-muted)', fontSize: '0.8125rem' }}>
          {t('common.studio.settings.appearanceOut')}
        </p>
      </div>
    </div>
  );
}
